/**
 * MessageInput Component
 * User input area for typing and sending messages.
 */

import { useState, useCallback, type FormEvent, type KeyboardEvent } from 'react';
import { Button } from '@components/common/Button';
import { CHATBOT_STRINGS } from '../../constants';
import { SendIcon } from '../Icons';
import styles from './MessageInput.module.css';

/**
 * MessageInput component props.
 */
interface MessageInputProps {
  /** Input placeholder text */
  placeholder?: string;
  /** Whether input is disabled */
  disabled?: boolean;
  /** Compact mode for floating widget */
  compact?: boolean;
  /** Whether the AI is currently processing (show loading state) */
  isLoading?: boolean;
  /** Callback when a message is submitted */
  onSendMessage?: (message: string) => void;
}

/**
 * Message input component.
 * Renders text input field and send button with form submission handling.
 */
export const MessageInput = ({
  placeholder = CHATBOT_STRINGS.INPUT_PLACEHOLDER,
  disabled = false,
  compact = false,
  isLoading = false,
  onSendMessage,
}: MessageInputProps) => {
  const [inputValue, setInputValue] = useState('');

  const isDisabled = disabled || isLoading;
  const canSend = inputValue.trim().length > 0 && !isDisabled;

  const handleSubmit = useCallback(
    (e?: FormEvent) => {
      e?.preventDefault();
      if (!canSend || !onSendMessage) return;
      onSendMessage(inputValue.trim());
      setInputValue('');
    },
    [canSend, inputValue, onSendMessage]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  return (
    <form
      className={`${styles.inputContainer} ${compact ? styles.compact : ''}`}
      onSubmit={handleSubmit}
    >
      <div className={styles.inputWrapper}>
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={isLoading ? 'AI is thinking...' : placeholder}
          className={styles.input}
          disabled={isDisabled}
          aria-label={CHATBOT_STRINGS.ARIA_MESSAGE_INPUT}
          autoComplete="off"
        />
        <Button
          variant="primary"
          className={styles.sendButton}
          disabled={!canSend}
          aria-label={CHATBOT_STRINGS.ARIA_SEND_MESSAGE}
          type="submit"
        >
          <SendIcon size={20} />
        </Button>
      </div>
    </form>
  );
};
